import { useMemo } from 'react';
import styled from '@emotion/styled/macro';
import {
  Button,
  tokens,
  SvgIcon,
  SvgNames,
  useSingletonMobileMediaQuery,
  MOBILE_BREAKPOINT,
} from '@sunrun/experience-ui-components';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import AnalyticsCircles from 'assets/images/analytics-circles.svg';

const SalesSection = () => {
  const isMobile = useSingletonMobileMediaQuery();
  const onNavigateToAnalytics = useMemo(() => {
    const baseUrl =
      getEnvironment() === 'production'
        ? 'https://sunrunone.com'
        : 'https://staging.sunrunone.com';
    return () =>
      window.parent.postMessage(
        {
          type: 'COMMAND:CHANGE_URL',
          source: 'Dashboard',
          payload: {
            url: baseUrl + '/analytics',
          },
        },
        '*',
      );
  }, []);

  return (
    <SalesSectionContainer>
      <Column>
        <SalesVisual className="mobile" isMobile={isMobile}>
          <StyledImg
            src={AnalyticsCircles}
            alt="Analytics Circles"
            isMobile={isMobile}
          />
        </SalesVisual>
        <SalesHeader isMobile={isMobile}>
          <SalesTitle>
            <SalesIcon>
              <SvgIcon name={SvgNames.Sparkles} height={24} width={24} />
            </SalesIcon>
            Track Your Sales. Crush Your Goals.
          </SalesTitle>
        </SalesHeader>

        <SalesDescription>
          See your performance at a glance, stay on top of your sales goals, and
          stack up against your team.
        </SalesDescription>

        <SalesButton
          size="sm"
          color="secondary"
          onClick={onNavigateToAnalytics}>
          Go to Analytics
        </SalesButton>
      </Column>
      <Column className="non-mobile">
        <SalesVisual isMobile={isMobile}>
          <StyledImg
            src={AnalyticsCircles}
            alt="Analytics Circles"
            isMobile={isMobile}
          />
        </SalesVisual>
      </Column>
    </SalesSectionContainer>
  );
};

// Styled components
const SalesHeader = styled.div<{ isMobile: boolean }>`
  margin-bottom: ${({ isMobile }) => (isMobile ? '10px' : '1rem')};
`;

const SalesTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SalesIcon = styled.span`
  font-size: 1rem;
`;

const SalesDescription = styled.p`
  margin: 0 0 1rem 0;
  color: ${tokens.TEXT_LIGHT};
  font-size: 12px;
  flex: 1;
`;

const SalesButton = styled(Button)`
  margin-bottom: 1rem;
  width: 160px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const SalesVisual = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'flex-end')};
  align-items: center;
  flex: 1;
`;

const StyledImg = styled.img<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '68px' : 'inherit')};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SalesSectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  .mobile {
    display: none;
  }
  .non-mobile {
    display: flex;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile {
      display: flex;
      padding-bottom: 16px;
    }
    .non-mobile {
      display: none;
    }
  }
`;

export { SalesSection };
